//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState, useEffect, Fragment, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { VerifiedUserRounded } from '@material-ui/icons';
import Popover from '@material-ui/core/Popover';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import UpdateIcon from '@material-ui/icons/Update';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { PATH_DASHBOARD, PATH_JOB_CENTER } from '../../system/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCombinedDateTime, getUserDateFormat } from '../../common/dateHelper';
import {
    INTERVIEW_STATUS_PENDING,
    // DASHBOARD_MY_JOBS,
    INTERVIEW_STATUS_CONFIRMED,
    INTERVIEW_STATUS_DECLINED,
    INTERVIEW_STATUS_RESCHEDULE_JS,
    INTERVIEW_STATUS_RESCHEDULE_EMP,
    DASHBOARD_ID_MY_JOBS,
    ANALYTICS_EVENT_INTERVIEW_CONFIRMED,
    ANALYTICS_EVENT_INTERVIEW_DECLINED,
    ANALYTICS_EVENT_VIRTUAL_INTERVIEW_CONFIRMED,
    ANALYTICS_EVENT_PHYSICAL_INTERVIEW_CONFIRMED,
    INTERVIEW_STATUS_CANCELLED,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveNote, confirmInterview, declineInterview, saveRescheduleInterviewRequest, markInterviewRead } from '../../common/services/empMyCalendarServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdBox from '../../common/elemental/AfitdBox';
import InterviewDialog from '../../empTalentCenter/subComponents/InterviewDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import InterviewActionButton from './InterviewActionButton';
import InterviewReschedule from './InterviewReschedule';
import InterviewDecline from './InterviewDecline';
import InterviewNote from './InterviewNote';
import JoinInterviewButton from './JoinInterviewButton';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import { differenceInMilliseconds, addMilliseconds, differenceInHours } from 'date-fns';
import _get from 'lodash/get';
// import _set from 'lodash/set';
import _isEqual from 'lodash/isEqual';
import _defaultTo from 'lodash/defaultTo';
import _isEmpty from 'lodash/isEmpty';
// import { cs } from 'date-fns/locale';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    popoverPaper: {
        borderRadius: '10px',
    },
    root: {
        backgroundColor: '#FFFFFF',
        width: '508px',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('sm')]: { width: '334px' },
    },
    // ----------------------- Header
    headerRow: {
        display: 'flex',
    },
    headerLeft: {
        width: '88px',
    },
    interviewStatusIcon: {
        marginTop: '24px',
        marginLeft: '24px',
        fontSize: 44,
        [theme.breakpoints.down('sm')]: { fontSize: 26 },
    },
    interviewStatusIconRoot: {
        width: '22px',
        height: '22px',
    },
    pendingIconContainer: { color: '#F0F2F6' },
    rescheduleIconContainer: { color: '#FC821F' },
    confirmedIconContainer: { color: '#FC821F' },
    declinedIconContainer: { color: '#F0F2F6' },
    headerRight: {
        display: 'flex',
    },
    interviewHeaderContainer: {
        // width: '364px',
        margin: '24px 0 4px 0',
        [theme.breakpoints.up('md')]: { width: '364px' },
    },
    closeIconButtonContainer: {
        width: '56px',
        marginTop: '4px',
        marginBottom: '4px',
        textAlign: 'center',
    },
    closeIconButton: {
        color: 'rgba(17, 30, 20, 0.4)',
    },
    // ----------------------- Job Title
    jobTitleRow: {
        // marginLeft: '88px',
    },
    // ----------------------- POC Name
    pocNamesRow: {
        // marginLeft: '88px',
    },
    // ----------------------- Time
    timeRow: {
        display: 'flex',
        marginTop: '26px',
    },
    timeLeft: {
        display: 'flex',
        width: '88px',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: { width: 70 },
    },
    highlightOffIconContainer: {
        color: '#F0F2F6',
    },
    timeRight: {
        display: 'flex',
    },
    // ----------------------- Location
    locationContainerRow: {
        marginLeft: '88px',
        marginTop: '6px',
        [theme.breakpoints.down('sm')]: { marginLeft: 70 },
    },
    // ----------------------- Date
    dateContainerRow: {
        marginLeft: '88px',
        marginTop: '6px',
        [theme.breakpoints.down('sm')]: { marginLeft: 70 },
    },
    // ----------------------- Edit Interview
    editInterviewContainer: {
        marginLeft: '88px',
        marginTop: '6px',
        [theme.breakpoints.down('sm')]: { marginLeft: 70 },
    },
    // ----------------------- Reschedule block
    interviewRescheduleRow: {
        display: 'flex',
        marginTop: '23px',
    },
    interviewRescheduleLeft: {
        display: 'flex',
        width: '88px',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: { marginLeft: 70 },
    },
    updateIconContainer: {
        marginTop: '30px',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    // ----------------------- Decline block
    interviewDeclineRow: {
        display: 'flex',
        marginTop: '23px',
    },
    interviewDeclineLeft: {
        display: 'flex',
        width: '88px',
        justifyContent: 'center',
    },
    highlightOffIconDeclineContainer: {
        marginTop: '30px',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    // updateIconContainer: {
    //     marginTop: '30px',
    //     color: 'rgba(17, 30, 20, 0.4)',
    // },
    // ----------------------- Note
    interviewNoteRow: {
        marginLeft: '88px',
        [theme.breakpoints.down('sm')]: { width: '100%', marginLeft: '0px' },
    },
    // ----------------------- Buttons
    buttonsRow: {
        display: 'flex',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
    },
    buttonsLeft: {
        width: '88px',
        marginTop: '24px',
        [theme.breakpoints.down('sm')]: { marginTop: '8px' },
    },
    noteIconButtonContainer: {
        flexGrow: 1,
    },
    noteIconButton: {
        width: '40px',
        height: '40px',
        marginLeft: '24px',
        marginBottom: '24px',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
    },
    buttonsRight: {
        flexGrow: 1,
        margin: '24px 24px 24px 0',
        display: 'flex',
        justifyContent: 'space-between',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse', margin: '24px' },
    },
    rescheduleButtonContainer: {
        width: '144px',
        [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 4 },
    },
    declineButtonContainer: {
        width: '118px',
        // [theme.breakpoints.down('sm')]: { width: '100%', marginLeft: 4 },
    },
    confirmButtonContainer: {
        // width: '118px',
        [theme.breakpoints.up('md')]: { width: '118px' },
        [theme.breakpoints.down('sm')]: { paddingBottom: 16 },
    },
    sendButtonContainer: {
        width: '118px',
    },
    // ----------------------- Misc
    hidden: {
        display: 'none',
    },
    editInterview: { textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' },
    declineReasonTextHeader: { fontWeight: 'bold', textDecoration: 'underline' },
    declineReasonText: { textDecoration: 'underline white solid' },

    inline: { display: 'flex', alignItems: 'center' },
    interviewHeaderText: { marginRight: 10, lineHeight: 1.5 },
    verifyIcon: { color: '#1D8A8B', fontSize: '21px', position: 'absolute', marginTop: '3px' },
    twoButtonsContainer: { display: 'flex' },
    noteInterviewButtons: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexDirection: 'row-reverse' },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, isEmployer, userDateFormat, interviewData, isNoteEnabled, isRescheduleEnabled, isInterviewActive, getUpdatedRef } = params;
    const {
        setInterviewCardOpen,
        setIsNoteEnabled,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        setIsRescheduleEnabled,
        isDeclineEnabled,
        setIsDeclineEnabled,
        handleClickSendButton,
        setProposedStartDate,
        setProposedStartTime,
        setDeclineReason,
        declineReason,
        isDeclineDialogOpen,
        setIsDeclineDialogOpen,
        editInterviewModalOpen,
        setEditInterviewModalOpen,
        handleClickJobTitleLink,
        isEmp,
        isEmpBadge,
    } = params;
    const { open, onClose, anchorEl, classes, note = '', detachInterviewCardListener } = props;
    const {
        popoverPaper: popoverPaperCls,
        editInterviewContainer: editInterviewContainerCls,
        timeText: timeTextCls,
        highlightOffIconContainer: highlightOffIconContainerCls,
        highlightOffIconDeclineContainer: highlightOffIconDeclineContainerCls,
        updateIconContainer: updateIconContainerCls,
        pendingIconContainer: pendingIconContainerCls,
        rescheduleIconContainer: rescheduleIconContainerCls,
        confirmedIconContainer: confirmedIconContainerCls,
        declinedIconContainer: declinedIconContainerCls,
        rescheduleButtonContainer: rescheduleButtonContainerCls,
        declineButtonContainer: declineButtonContainerCls,
        confirmButtonContainer: confirmButtonContainerCls,
        sendButtonContainer: sendButtonContainerCls,
        hidden: hiddenCls,
        spacedItem: spacedItemCls,
        editInterview: editInterviewCls,
        verifyIcon,
        ...otherClasses
    } = classes;

    //--------------------------------------------------------------------
    // TEMP data
    // const interviewStatus = INTERVIEW_STATUS_PENDING;
    // const now = new Date();
    // const startDt = now;
    // const companyName = 'The Matrix';
    // const pocName = 'Thomas Anderson';
    // const jobTitle = 'Business Analyst';
    // const virtual = Math.floor(Math.random() * Math.floor(10)) >= 5;
    // const address = "700 L'Enfant Plaza SW, Washington, DC 20024";
    // const note = 'The Matrix has me';
    //--------------------------------------------------------------------

    const interviewId = _get(interviewData, 'interviewId');
    const interviewStatus = _get(interviewData, 'status');
    const startDt = _get(interviewData, 'startDt');
    const endDt = _get(interviewData, 'endDt');
    const companyName = _get(interviewData, 'companyName');
    const interviewers = _get(interviewData, 'interviewers');
    const interviewersNames = interviewers?.map(el => el.name);
    const jobSeekerId = _get(interviewData, 'jobSeekerId');
    const jobSeekerName = _get(interviewData, 'jobSeekerName');
    const jobId = _get(interviewData, 'jobId');
    const jobTitle = _get(interviewData, 'jobTitle');
    const virtual = _get(interviewData, 'virtual');
    const externalInterviewLink = _get(interviewData, 'externalInterviewLink');
    const streetAddress1 = _get(interviewData, 'streetAddress1');
    const streetAddress2 = _get(interviewData, 'streetAddress2');
    const city = _get(interviewData, 'city');
    const state = _get(interviewData, 'state');
    const postalCode = _get(interviewData, 'postalCode');
    const jsAccountDeleteDt = _get(interviewData, 'jsAccountDeleteDt');
    // const note = _defaultTo(_get(interviewData, 'note'), '');
    const declineReasonValue = _defaultTo(_get(interviewData, 'declineReason'), '');
    // interview will only be cancelled when job seeker deleted his account or when employer unsubscribed -el 20230826
    const cancelReasonText = jsAccountDeleteDt ? 'This job seeker is no longer available.' : 'This employer is no longer available.';
    const interviewHeader = getInterviewHeader(isEmployer, interviewData);
    const interviewTimeText = startDt ? `${format(startDt, 'p')} - ${format(endDt, 'p')}` : '';
    const interviewDateText = startDt ? format(startDt, `eeee, ${userDateFormat}`) : '';
    const interviewLocation = virtual
        ? { virtual, interviewId, jobTitle, startDt, endDt, interviewStatus, companyName, externalInterviewLink }
        : { streetAddress1, streetAddress2, city, state, postalCode };
    const interviewRescheduleIsHidden = !isRescheduleEnabled;
    const interviewDeclineIsHidden = !isDeclineEnabled;
    const noteIsHidden = !(isNoteEnabled || note);
    // const noteHelperText = `${note ? note.length : 0}/200`;
    // const addButtonIsHidden = !isNoteEnabled;
    const rescheduleButtonDisabled =
        getRescheduleButtonDisabled({ isEmployer, interviewStatus, isNoteEnabled, isRescheduleEnabled, isDeclineEnabled, startDt }) || isInterviewActive;
    const declineButtonDisabled = getDeclineButtonDisabled({ interviewStatus, isNoteEnabled, isRescheduleEnabled, isDeclineEnabled }) || isInterviewActive;
    const confirmButtonDisabled = getConfirmedButtonDisabled({ isEmployer, interviewStatus, isNoteEnabled }) || isInterviewActive;
    const sendButtonDisabled = getSendButtonDisabled({ interviewStatus, isNoteEnabled, isRescheduleEnabled, isDeclineEnabled, declineReason }) || isInterviewActive;

    let [confirmButtonContainerIsHidden, sendButtonContainerIsHidden] = [false, false];
    let [isStatusReschedule, isStatusDeclined, isStatusConfirmed, isStatusPending, isStatusCancelled] = [false, false, false, false, false];

    switch (interviewStatus) {
        case INTERVIEW_STATUS_RESCHEDULE_JS: {
            isStatusReschedule = true;
            confirmButtonContainerIsHidden = !isEmployer || isRescheduleEnabled || isDeclineEnabled;
            sendButtonContainerIsHidden = !isRescheduleEnabled && !isDeclineEnabled;
            break;
        }
        case INTERVIEW_STATUS_RESCHEDULE_EMP: {
            isStatusReschedule = true;
            confirmButtonContainerIsHidden = isEmployer || isRescheduleEnabled || isDeclineEnabled;
            sendButtonContainerIsHidden = !isRescheduleEnabled && !isDeclineEnabled;
            break;
        }
        case INTERVIEW_STATUS_DECLINED: {
            isStatusDeclined = true;
            sendButtonContainerIsHidden = true;
            break;
        }
        case INTERVIEW_STATUS_CONFIRMED: {
            isStatusConfirmed = true;
            if (isRescheduleEnabled || isDeclineEnabled) {
                confirmButtonContainerIsHidden = true;
            } else {
                sendButtonContainerIsHidden = true;
            }
            break;
        }
        case INTERVIEW_STATUS_CANCELLED: {
            isStatusCancelled = true;
            sendButtonContainerIsHidden = true;
            break;
        }
        default: {
            // INTERVIEW_STATUS_PENDING
            isStatusPending = true;
            if (isRescheduleEnabled || isDeclineEnabled) {
                confirmButtonContainerIsHidden = true;
            } else {
                sendButtonContainerIsHidden = true;
            }
        }
    }

    let defns = {
        interviewCardPopover: {
            open,
            onClose,
            anchorEl,
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            classes: { paper: popoverPaperCls },
        },
        statusIconContainer: {
            className: cx({
                [rescheduleIconContainerCls]: isStatusReschedule,
                [declinedIconContainerCls]: isStatusDeclined || isStatusCancelled,
                [confirmedIconContainerCls]: isStatusConfirmed,
                [pendingIconContainerCls]: isStatusPending,
            }),
        },
        interviewStatusIcon: {
            interviewStatus,
        },
        rescheduleButtonContainer: {
            className: cx(rescheduleButtonContainerCls),
        },
        rescheduleButton: {
            startIcon: <UpdateIcon />,
            label: 'Reschedule',
            color: 'default',
            fullWidth: true,
            disabled: rescheduleButtonDisabled,
            onClick: handleClickRescheduleButton.bind(null, setIsRescheduleEnabled),
        },
        closeIconButton: {
            onClick: handleClickCloseIconButton.bind(null, setInterviewCardOpen, detachInterviewCardListener),
        },
        declineButtonContainer: {},
        declineButton: {
            startIcon: <HighlightOffIcon />,
            label: 'Decline',
            color: 'default',
            fullWidth: true,
            disabled: declineButtonDisabled,
            // onClick: handleClickDeclineButton.bind(null, interviewId, setInterviewCardOpen, setSnackbarMessage, detachInterviewCardListener),
            onClick: handleClickDeclineButton.bind(null, setIsDeclineEnabled),
        },
        confirmButtonContainer: {
            className: cx(confirmButtonContainerCls, { [hiddenCls]: confirmButtonContainerIsHidden }),
        },
        confirmButton: {
            startIcon: <CheckCircleIcon />,
            label: 'Confirm',
            color: 'primary',
            fullWidth: true,
            disabled: confirmButtonDisabled,
            onClick: handleClickConfirmButton.bind(
                null,
                isEmployer,
                interviewId,
                setInterviewCardOpen,
                virtual,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                detachInterviewCardListener,
            ),
        },
        sendButtonContainer: {
            className: cx(sendButtonContainerCls, { [hiddenCls]: sendButtonContainerIsHidden }),
        },
        sendButton: {
            startIcon: <SendIcon />,
            label: 'Send',
            color: 'secondary',
            fullWidth: true,
            disabled: sendButtonDisabled,
            onClick: handleClickSendButton.bind(
                null,
                interviewId,
                startDt,
                endDt,
                isRescheduleEnabled,
                setIsRescheduleEnabled,
                isDeclineEnabled,
                setIsDeclineEnabled,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                setIsDeclineDialogOpen,
            ),
        },
        interviewHeaderText: {
            variant: 'h6',
            children: (
                <span>
                    {interviewHeader}
                    {!isEmp && isEmpBadge && (
                        <>
                            &nbsp;&nbsp;
                            <VerifiedUserRounded className={verifyIcon} />
                        </>
                    )}
                </span>
            ),
        },
        interviewersNameText: {
            variant: 'body2',
            value: interviewers?.length ? `w/: ${interviewersNames.join(', ')}` : '',
        },
        jobTitleLink: {
            onClick: handleClickJobTitleLink,
        },
        jobTitleText: {
            variant: 'body2',
            color: 'inherit',
            clsLink: true, // pass this in to AfitdTypography because for some reasons the Link component is missing the 'pointer' cursor
            value: jobTitle,
        },
        interviewTimeText: {
            variant: 'body1',
            clsLightBold: true,
            clsGrayColor: isRescheduleEnabled,
            clsStrikethrough: isRescheduleEnabled,
            value: interviewTimeText,
        },
        rescheduleText: {
            variant: 'body1',
            clsLightBold: true,
            clsGrayColor: true,
            isHidden: !isRescheduleEnabled,
            value: 'Reschedule',
        },
        interviewLocation: {
            variant: 'body2',
            data: interviewLocation,
            onClick: getUpdatedRef,
        },
        declineReasonText: {
            variant: 'body1',
            component: 'span',
            value: ' ' + declineReasonValue,
        },
        cancelReasonText: {
            variant: 'body1',
            component: 'span',
            value: cancelReasonText,
        },
        editInterviewContainer: {
            className: cx(editInterviewContainerCls, { [hiddenCls]: !isEmployer }),
        },
        editInterview: {
            variant: 'caption',
            value: 'Edit Invitation',
            onClick: () => setEditInterviewModalOpen(true),
            className: cx(editInterviewCls, { [hiddenCls]: isStatusDeclined || isStatusCancelled }),
        },
        interviewDateText: {
            variant: 'body2',
            // clsGrayColor: true,
            clsStrikethrough: isRescheduleEnabled,
            value: interviewDateText,
        },
        noteIconButton: {
            onClick: handleClickNoteIconButton.bind(null, setIsNoteEnabled, note),
        },
        addNoteToolTip: {
            title: 'Add Note',
            placement: 'top',
            arrow: true,
        },
        highlightOffIconContainer: {
            className: cx(highlightOffIconContainerCls, { [hiddenCls]: !isRescheduleEnabled }),
        },
        updateIconContainer: {
            className: cx(updateIconContainerCls, { [hiddenCls]: !isRescheduleEnabled }),
        },
        interviewReschedule: {
            isHidden: interviewRescheduleIsHidden,
            originalStartDt: startDt,
            originalEndDt: endDt,
            startDateOnModify: setProposedStartDate,
            startTimeOnModify: setProposedStartTime,
            onCancel: handleCancelReschedule.bind(null, setIsRescheduleEnabled),
        },
        interviewNote: {
            value: note,
            isEnabled: isNoteEnabled,
            setIsEnabled: setIsNoteEnabled,
            setIsNoteEnabled,
            isHidden: noteIsHidden,
            onSave: handleSaveNote.bind(null, isEmployer, interviewId, setIsNoteEnabled, note, setSnackbarMessage, setSnackbarErrorMessage),
            onCancel: handleCancelNote.bind(null, setIsNoteEnabled),
        },
        interviewDecline: {
            isHidden: interviewDeclineIsHidden,
            isEnabled: isDeclineEnabled,
            setIsEnabled: setIsDeclineEnabled,
            declineReasonOnModify: setDeclineReason,
            onCancel: handleCancelDecline.bind(null, setIsDeclineEnabled),
        },
        highlightOffIconDeclineContainer: {
            className: cx(highlightOffIconDeclineContainerCls, { [hiddenCls]: !isDeclineEnabled }),
        },
        declineDialog: {
            open: isDeclineDialogOpen,
            onClose: handleCloseDeclineDialog.bind(null, setIsDeclineDialogOpen),
        },
        declineDialogCancelButton: {
            color: 'default',
            onClick: handleCloseDeclineDialog.bind(null, setIsDeclineDialogOpen),
        },
        declineDialogYesButton: {
            color: 'secondary',
            autoFocus: true,
            onClick: handleClickDeclineDialogYes.bind(
                null,
                isEmployer,
                interviewId,
                setIsDeclineEnabled,
                declineReason,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                setIsDeclineDialogOpen,
            ),
        },
        editInterviewDialog: {
            open: editInterviewModalOpen,
            onClose: () => setEditInterviewModalOpen(false),
            interviewData,
            jobSeekerId,
            jobSeekerName,
            jobId,
            jobTitle,
        },
        confirmDeclineDialogContentText: {
            value: `The decline will be final and the reason will be sent to the ${isEmployer ? 'job seeker' : 'employer'}.`,
        },
    };

    setClassNameInDefns(defns, otherClasses);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getRescheduleButtonDisabled = ({ isEmployer, interviewStatus, isNoteEnabled, isRescheduleEnabled, isDeclineEnabled, startDt }) => {
    return (
        isNoteEnabled ||
        isRescheduleEnabled ||
        isDeclineEnabled ||
        (isEmployer && interviewStatus === INTERVIEW_STATUS_RESCHEDULE_EMP) ||
        (!isEmployer && interviewStatus === INTERVIEW_STATUS_RESCHEDULE_JS) ||
        (!isEmployer && interviewStatus === INTERVIEW_STATUS_CONFIRMED && isAfter(new Date(), startDt)) ||
        interviewStatus === INTERVIEW_STATUS_DECLINED ||
        interviewStatus === INTERVIEW_STATUS_CANCELLED
    );
};

const getDeclineButtonDisabled = ({ interviewStatus, isNoteEnabled, isRescheduleEnabled, isDeclineEnabled }) => {
    return isNoteEnabled || isRescheduleEnabled || isDeclineEnabled || interviewStatus === INTERVIEW_STATUS_DECLINED || interviewStatus === INTERVIEW_STATUS_CANCELLED;
};

const handleCancelReschedule = setIsRescheduleEnabled => {
    setIsRescheduleEnabled(false);
};

const handleCancelDecline = setIsDeclineEnabled => {
    setIsDeclineEnabled(false);
};

const getConfirmedButtonDisabled = ({ isEmployer, interviewStatus, isNoteEnabled }) => {
    return (
        (isEmployer &&
            (interviewStatus === INTERVIEW_STATUS_PENDING ||
                interviewStatus === INTERVIEW_STATUS_RESCHEDULE_EMP ||
                interviewStatus === INTERVIEW_STATUS_CONFIRMED ||
                interviewStatus === INTERVIEW_STATUS_DECLINED)) ||
        interviewStatus === INTERVIEW_STATUS_CANCELLED ||
        (!isEmployer &&
            (interviewStatus === INTERVIEW_STATUS_RESCHEDULE_JS ||
                interviewStatus === INTERVIEW_STATUS_CONFIRMED ||
                interviewStatus === INTERVIEW_STATUS_DECLINED ||
                interviewStatus === INTERVIEW_STATUS_CANCELLED)) ||
        !interviewStatus
    );
};

const getSendButtonDisabled = ({ interviewStatus, isRescheduleEnabled, isDeclineEnabled, declineReason }) => {
    return !(isRescheduleEnabled || (isDeclineEnabled && !_isEmpty(declineReason)));
};

const handleClickCloseIconButton = (setInterviewCardOpen, detachInterviewCardListener) => {
    detachInterviewCardListener();
    setInterviewCardOpen(false);
};

const handleClickRescheduleButton = setIsRescheduleEnabled => {
    setIsRescheduleEnabled(true);
};

const handleClickConfirmButton = (isEmployer, interviewId, setInterviewCardOpen, virtual, setSnackbarMessage, setSnackbarErrorMessage, detachInterviewCardListener) => {
    confirmInterview({
        isEmployer,
        interviewId,
        setInterviewCardOpen,
        callback: () => {
            logEvent(ANALYTICS_EVENT_INTERVIEW_CONFIRMED);
            if (virtual) logEvent(ANALYTICS_EVENT_VIRTUAL_INTERVIEW_CONFIRMED);
            else logEvent(ANALYTICS_EVENT_PHYSICAL_INTERVIEW_CONFIRMED);
        },
        setSnackbarMessage,
        setSnackbarErrorMessage,
        detachInterviewCardListener,
    });
};

// const handleClickDeclineButton = (interviewId, setInterviewCardOpen, setSnackbarMessage, detachInterviewCardListener) => {
//     declineInterview(interviewId, setInterviewCardOpen, setSnackbarMessage, detachInterviewCardListener);
// };
const handleClickDeclineButton = setIsDeclineEnabled => {
    setIsDeclineEnabled(true);
};

// const handleClickSendButton = (interviewId, setIsRescheduleEnabled, proposedStartDt, proposedEndDt, setSnackbarMessage) => {
//     saveRescheduleInterviewRequest({ interviewId, setIsRescheduleEnabled, proposedStartDt, proposedEndDt, setSnackbarMessage });
// };

const handleClickNoteIconButton = (setIsNoteEnabled, note) => {
    setIsNoteEnabled(true);
};
const handleCancelNote = setIsNoteEnabled => {
    setIsNoteEnabled(false);
};

const handleSaveNote = (isEmployer, interviewId, setIsNoteEnabled, prevNote, setSnackbarMessage, setSnackbarErrorMessage, note) => {
    // Do not save if both original note and new note are undefined/null/empty, or if new note is same as original
    if ((!prevNote && !note) || _isEqual(prevNote, note)) {
        setIsNoteEnabled(false);
    } else {
        saveNote({ isEmployer, interviewId, setIsNoteEnabled, setSnackbarMessage, setSnackbarErrorMessage, note });
    }
};

const handleCloseDeclineDialog = setIsDeclineDialogOpen => {
    setIsDeclineDialogOpen(false);
};

const handleClickDeclineDialogYes = (isEmployer, interviewId, setIsDeclineEnabled, declineReason, setSnackbarMessage, setSnackbarErrorMessage, setIsDeclineDialogOpen) => {
    declineInterview({
        isEmployer,
        interviewId,
        setIsDeclineEnabled,
        declineReason,
        callback: () => {
            logEvent(ANALYTICS_EVENT_INTERVIEW_DECLINED);
        },
        setSnackbarMessage,
        setSnackbarErrorMessage,
    });

    setIsDeclineDialogOpen(false);
};

const getInterviewHeader = (isEmployer, data) => {
    return `Interview with ${isEmployer ? _get(data, 'jobSeekerName') : _get(data, 'companyName')}`;
};

const InterviewStatusIcon = props => {
    const { interviewStatus, ...otherProps } = props;

    switch (interviewStatus) {
        case INTERVIEW_STATUS_PENDING:
            return <HelpOutlineIcon {...otherProps} />;
        case INTERVIEW_STATUS_CONFIRMED:
            return <CheckCircleIcon {...otherProps} />;
        case INTERVIEW_STATUS_RESCHEDULE_JS:
        case INTERVIEW_STATUS_RESCHEDULE_EMP:
            return <UpdateIcon {...otherProps} />;
        case INTERVIEW_STATUS_DECLINED:
        case INTERVIEW_STATUS_CANCELLED:
            return <HighlightOffIcon {...otherProps} />;
        default:
            return <HelpOutlineIcon {...otherProps} />;
    }
};

const InterviewLocation = props => {
    const { variant, data = {}, onClick } = props;
    const { virtual } = data;

    if (virtual) {
        const { interviewId, jobTitle, companyName, startDt, endDt, interviewStatus, externalInterviewLink } = data;
        const INTERVIEW_LINK_EXPIRATION = 24;
        const joinInterviewButtonDisabled = differenceInHours(new Date(), startDt) > INTERVIEW_LINK_EXPIRATION - 1;
        return (
            <Fragment>
                <AfitdTypography variant={variant} value="Virtual" />
                <JoinInterviewButton
                    id={interviewId}
                    title={jobTitle}
                    companyName={companyName}
                    startDt={startDt}
                    endDt={endDt}
                    interviewStatus={interviewStatus}
                    onClick={onClick}
                    disabled={joinInterviewButtonDisabled}
                    externalInterviewLink={externalInterviewLink}
                />
            </Fragment>
        );
    } else {
        const streetAddress1 = _defaultTo(_get(data, 'streetAddress1'), '');
        const streetAddress2 = _defaultTo(_get(data, 'streetAddress2'), '');
        const city = _defaultTo(_get(data, 'city'), '');
        const state = _defaultTo(_get(data, 'state'), '');
        const postalCode = _defaultTo(_get(data, 'postalCode'), '');

        // format to 'city, state zip'
        let cityStateZip = '';
        cityStateZip = state ? (city ? `${city}, ${state}` : state) : city ? city : '';
        cityStateZip = postalCode ? (cityStateZip ? `${cityStateZip} ${postalCode}` : cityStateZip) : cityStateZip ? cityStateZip : '';

        return (
            <Fragment>
                <AfitdTypography variant={variant} value={streetAddress1} isHidden={!!!streetAddress1} />
                <AfitdTypography variant={variant} value={streetAddress2} isHidden={!!!streetAddress2} />
                <AfitdTypography variant={variant} value={cityStateZip} isHidden={!!!cityStateZip} />
            </Fragment>
        );
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const InterviewCard = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const context = useContext(LayoutContext);
    const { setCurrentDashboard, setSnackbarMessage, setSnackbarErrorMessage } = context;
    const appContext = useContext(AppContext);
    const { currentEmployer, employerId, userId, isEmp } = appContext;
    const userDateFormat = getUserDateFormat(appContext);
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // "instance variables" that do not cause rerender when changed
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const open = _get(props, 'open');
    const interviewData = _get(props, 'data');
    const startDt = _get(props, 'data.startDt');
    const endDt = _get(props, 'data.endDt');
    const setInterviewCardOpen = _get(props, 'setInterviewCardOpen');
    const setInterviewData = _get(props, 'setInterviewCardData');
    const interviewId = _get(interviewData, 'interviewId');
    const interviewStatus = _get(interviewData, 'status');
    const jobId = _get(interviewData, 'jobId');
    const isEmpBadge = _get(interviewData, 'isEmpBadge');
    // const jobTitle = _get(interviewData, 'jobTitle');
    // const tier = _get(currentEmployer, 'tier');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isNoteEnabled, setIsNoteEnabled] = useState(false);
    const [isRescheduleEnabled, setIsRescheduleEnabled] = useState(false);
    const [isDeclineEnabled, setIsDeclineEnabled] = useState(false);
    const [proposedStartDate, setProposedStartDate] = useState(startDt);
    const [proposedStartTime, setProposedStartTime] = useState(endDt);
    const [declineReason, setDeclineReason] = useState('');
    const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
    const [editInterviewModalOpen, setEditInterviewModalOpen] = useState(false);

    const history = useHistory();

    const [isInterviewActive, setIsInterviewActive] = useState(false);

    const getUpdatedRef = useCallback(ref => {
        setIsInterviewActive(ref);
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isEmployer = !!currentEmployer;

    //---------------------------------------------------------------------
    // useCallback hook
    //---------------------------------------------------------------------
    const handleClickSendButton = useCallback(
        (
            interviewId,
            startDt,
            endDt,
            isRescheduleEnabled,
            setIsRescheduleEnabled,
            isDeclineEnabled,
            setIsDeclineEnabled,
            setSnackbarMessage,
            setSnackbarErrorMessage,
            setIsDeclineDialogOpen,
        ) => {
            if (isRescheduleEnabled === true) {
                const proposedStartDt = getCombinedDateTime(proposedStartDate, proposedStartTime);
                const durationMs = differenceInMilliseconds(endDt, startDt);
                const proposedEndDt = addMilliseconds(proposedStartDt, durationMs);

                saveRescheduleInterviewRequest({
                    isEmployer,
                    interviewId,
                    setIsRescheduleEnabled,
                    startDt,
                    endDt,
                    proposedStartDt,
                    proposedEndDt,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                });
            }
            if (isDeclineEnabled === true) {
                setIsDeclineDialogOpen(true);
            }
        },
        [isEmployer, proposedStartDate, proposedStartTime],
    );

    const handleClickJobTitleLink = useCallback(() => {
        if (isEmployer) {
            history.push(`${PATH_DASHBOARD}/${DASHBOARD_ID_MY_JOBS}`, {
                pathname: `${PATH_DASHBOARD}/${DASHBOARD_ID_MY_JOBS}`,
                state: { jobId },
            });
        } else {
            history.push(`${PATH_JOB_CENTER}`, {
                pathname: `${PATH_JOB_CENTER}`,
                state: { jobId },
            });
        }
    }, [history, isEmployer, jobId]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // mark confirmed/declined interview read
    useEffect(() => {
        if (open && interviewStatus) {
            const isConfirmed = interviewStatus === INTERVIEW_STATUS_CONFIRMED;
            const isDeclined = interviewStatus === INTERVIEW_STATUS_DECLINED;
            if (isConfirmed || isDeclined) {
                markInterviewRead({ dashboardId: isEmployer ? employerId : userId, interviewId, isConfirmed, isDeclined });
            }
        }
    }, [open, interviewStatus, interviewId, isEmployer, employerId, userId]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        isEmployer,
        userDateFormat,
        setCurrentDashboard,
        interviewData,
        setInterviewCardOpen,
        setInterviewData,
        isNoteEnabled,
        setIsNoteEnabled,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        isRescheduleEnabled,
        setIsRescheduleEnabled,
        isDeclineEnabled,
        setIsDeclineEnabled,
        handleClickSendButton,
        setProposedStartDate,
        setProposedStartTime,
        setDeclineReason,
        declineReason,
        isDeclineDialogOpen,
        setIsDeclineDialogOpen,
        editInterviewModalOpen,
        setEditInterviewModalOpen,
        isInterviewActive,
        getUpdatedRef,
        handleClickJobTitleLink,
        isEmp,
        isEmpBadge,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, tempInterviewData: interviewData }}>
            <Popover {...defns.interviewCardPopover}>
                <div {...defns.root}>
                    <div {...defns.headerRow}>
                        <div {...defns.headerLeft}>
                            <div {...defns.statusIconContainer}>
                                <InterviewStatusIcon {...defns.interviewStatusIcon} />
                            </div>
                        </div>
                        <div {...defns.headerRight}>
                            <div {...defns.interviewHeaderContainer}>
                                <div>
                                    <AfitdTypography {...defns.interviewHeaderText} />
                                </div>
                                <div {...defns.jobTitleRow}>
                                    <Link {...defns.jobTitleLink}>
                                        <AfitdTypography {...defns.jobTitleText} />
                                    </Link>
                                </div>
                                <div {...defns.pocNamesRow}>
                                    <AfitdTypography {...defns.interviewersNameText} />
                                </div>
                            </div>
                            <div {...defns.closeIconButtonContainer}>
                                <IconButton {...defns.closeIconButton}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                    <div {...defns.timeRow}>
                        <div {...defns.timeLeft}>
                            <div {...defns.highlightOffIconContainer}>
                                <HighlightOffIcon {...defns.highlightOffIcon} />
                            </div>
                        </div>
                        <div {...defns.timeRight}>
                            <AfitdTypography {...defns.interviewTimeText} />
                            <span>&nbsp;</span>
                            <AfitdTypography {...defns.rescheduleText} />
                        </div>
                    </div>
                    <div {...defns.locationContainerRow}>
                        <InterviewLocation {...defns.interviewLocation} />
                    </div>
                    <div {...defns.dateContainerRow}>
                        <AfitdBox fontStyle="italic">
                            <AfitdTypography {...defns.interviewDateText} />
                        </AfitdBox>
                    </div>
                    <div {...defns.editInterviewContainer}>
                        <AfitdTypography {...defns.editInterview} />
                    </div>
                    {interviewStatus === INTERVIEW_STATUS_DECLINED && (
                        <div {...defns.dateContainerRow}>
                            <AfitdTypography {...defns.declineReasonTextHeader}>
                                Decline Reason:
                                <AfitdTypography {...defns.declineReasonText} />
                            </AfitdTypography>
                        </div>
                    )}
                    {interviewStatus === INTERVIEW_STATUS_CANCELLED && (
                        <div {...defns.dateContainerRow}>
                            <AfitdTypography {...defns.cancelReasonText}></AfitdTypography>
                        </div>
                    )}
                    <div {...defns.interviewRescheduleRow}>
                        <div {...defns.interviewRescheduleLeft}>
                            <div {...defns.updateIconContainer}>
                                <UpdateIcon {...defns.updateIcon} />
                            </div>
                        </div>
                        <div {...defns.interviewRescheduleRight}>
                            <InterviewReschedule {...defns.interviewReschedule} />
                        </div>
                    </div>
                    <div {...defns.interviewDeclineRow}>
                        <div {...defns.interviewDeclineLeft}>
                            <div {...defns.highlightOffIconDeclineContainer}>
                                <HighlightOffIcon {...defns.highlightOffIconDecline} />
                            </div>
                        </div>
                        <div>
                            <InterviewDecline {...defns.interviewDecline} />
                        </div>
                    </div>
                    {smMatches ? (
                        <div {...defns.noteInterviewButtons}>
                            <div {...defns.interviewNoteRow}>
                                <InterviewNote {...defns.interviewNote} />
                            </div>
                            <div {...defns.buttonsLeft}>
                                <div {...defns.noteIconButtonContainer}>
                                    <Tooltip {...defns.addNoteToolTip}>
                                        <IconButton {...defns.noteIconButton}>
                                            <FormatQuoteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div {...defns.interviewNoteRow}>
                            <InterviewNote {...defns.interviewNote} />
                        </div>
                    )}
                    <div {...defns.buttonsRow}>
                        {smMatches ? null : (
                            <div {...defns.buttonsLeft}>
                                <div {...defns.noteIconButtonContainer}>
                                    <Tooltip {...defns.addNoteToolTip}>
                                        <IconButton {...defns.noteIconButton}>
                                            <FormatQuoteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                        {smMatches ? (
                            <div {...defns.buttonsRight}>
                                <div {...defns.twoButtonsContainer}>
                                    <div {...defns.rescheduleButtonContainer}>
                                        <InterviewActionButton {...defns.rescheduleButton} />
                                    </div>
                                    <div {...defns.declineButtonContainer} style={{ width: '100%', marginLeft: 4 }}>
                                        <InterviewActionButton {...defns.declineButton} />
                                    </div>
                                </div>
                                <div {...defns.confirmButtonContainer}>
                                    <InterviewActionButton {...defns.confirmButton} />
                                </div>
                                <div {...defns.sendButtonContainer}>
                                    <InterviewActionButton {...defns.sendButton} />
                                </div>
                            </div>
                        ) : (
                            <div {...defns.buttonsRight}>
                                <div {...defns.rescheduleButtonContainer}>
                                    <InterviewActionButton {...defns.rescheduleButton} />
                                </div>
                                <div {...defns.declineButtonContainer}>
                                    <InterviewActionButton {...defns.declineButton} />
                                </div>

                                <div {...defns.confirmButtonContainer}>
                                    <InterviewActionButton {...defns.confirmButton} />
                                </div>
                                <div {...defns.sendButtonContainer}>
                                    <InterviewActionButton {...defns.sendButton} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Popover>
            <Dialog aria-labelledby="decline-dialog" aria-describedby="decline-dialog" {...defns.declineDialog}>
                <DialogTitle id="alert-dialog-title">Decline Interview?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <AfitdTypography variant="body1" {...defns.confirmDeclineDialogContentText} />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <AfitdButton {...defns.declineDialogCancelButton}>Cancel</AfitdButton>
                    <AfitdButton {...defns.declineDialogYesButton}>Decline</AfitdButton>
                </DialogActions>
            </Dialog>
            {editInterviewModalOpen && <InterviewDialog {...defns.editInterviewDialog} />}
        </LayoutContext.Provider>
    );
};

export default withStyles(styles)(InterviewCard);
